/**
 * Focus a field once the sheet that holds it ends its open transition.
 *
 * A field focused while its sheet still slides in takes the caret at a moving position,
 * and a phone can refuse to raise the keyboard for it. The mobile app applies the same
 * rule in src/Hooks/UseFocusOnTransitionEnd.ts.
 */

// Covers an open that runs no transition, and a transitionend that never fires.
export const TRANSITION_END_FALLBACK_MS = 600;

/**
 * Call `focus` once: when a transition ends on an element that holds `field`, or when
 * the fallback delay runs out, whichever comes first.
 *
 * @param {Element} field The field, or an element inside the sheet that holds it.
 * @param {() => void} focus
 * @returns {() => void} Cancels the request. Call it when the sheet closes first.
 */
export function focusOnTransitionEnd(field, focus, fallbackMs = TRANSITION_END_FALLBACK_MS) {
    const doc = field.ownerDocument;
    let timer = null;

    const teardown = () => {
        clearTimeout(timer);
        doc.removeEventListener('transitionend', onTransitionEnd);
    };
    const run = () => {
        teardown();
        focus();
    };
    // A transition on a button or a child inside the sheet does not hold the field.
    const onTransitionEnd = (event) => {
        if (typeof event.target?.contains === 'function' && event.target.contains(field)) {
            run();
        }
    };

    doc.addEventListener('transitionend', onTransitionEnd);
    timer = setTimeout(run, fallbackMs);

    return teardown;
}
